import { useEffect, useState } from 'react'
import { pushProductName } from './uiConfig'

// Product wordmark + the app-wide FONT SYSTEM, driven live from QA.
//
// Fonts are organised into FIVE locked groups (Ken 2026-08-10):
//   Brand   — the wordmark, and only the wordmark.
//   Record  — verbatim source: email bodies, quoted history, signatures,
//             document text, subjects/filenames, sender names as written.
//   Summary — machine-authored prose: chron summaries, issue/kind labels,
//             matter names. The system's voice.
//   Chrome  — the product talking about itself: nav, buttons, folder names,
//             toggles, section/column/tile labels, page titles.
//   Data    — not language: identifiers, counts, timestamps, addresses.
//
// Each group picks a typeface from the unified FONTS registry below. Brand
// also carries the wordmark size; every other group controls font-family only
// (element pixel-sizes stay put so visual hierarchy is preserved). All of it
// persists to localStorage and updates live via a custom event.

export type FontKey =
  | 'instrument' | 'fraunces' | 'newsreader' | 'arial' | 'arialNarrow'
  | 'noto' | 'inter' | 'mono'

// Unified typeface registry — serif display faces (pulled in via the Google
// Fonts @import in index.css), two Arial system options, plus clean sans/mono
// faces. Any group may pick any of these.
export const FONTS: Record<FontKey, { label: string; family: string }> = {
  instrument:  { label: 'Instrument Serif', family: "'Instrument Serif', serif" },
  fraunces:    { label: 'Fraunces',         family: "'Fraunces', serif" },
  newsreader:  { label: 'Newsreader',       family: "'Newsreader', serif" },
  arial:       { label: 'Arial',            family: "Arial, Helvetica, sans-serif" },
  arialNarrow: { label: 'Arial Narrow',     family: "'Arial Narrow', Arial, sans-serif" },
  noto:        { label: 'Noto Sans',        family: "'Noto Sans', system-ui, sans-serif" },
  inter:       { label: 'Inter Tight',      family: "'Inter Tight', sans-serif" },
  mono:        { label: 'JetBrains Mono',   family: "'JetBrains Mono', monospace" },
}

// The five groups, in display order.
export type FontGroup = 'brand' | 'record' | 'summary' | 'chrome' | 'data'
export const FONT_GROUPS: { key: FontGroup; label: string; blurb: string }[] = [
  { key: 'brand',   label: 'Brand',   blurb: 'the wordmark, nothing else' },
  { key: 'record',  label: 'Record',  blurb: 'verbatim source — email/doc text, subjects, names' },
  { key: 'summary', label: 'Summary', blurb: 'machine prose — summaries, matter names, kind labels' },
  { key: 'chrome',  label: 'Chrome',  blurb: 'the product itself — nav, buttons, labels, titles' },
  { key: 'data',    label: 'Data',    blurb: 'not language — ids, counts, timestamps' },
]

// Fixed per Ken's spec — the wordmark color is not user-controlled.
export const PRODUCT_NAME_COLOR = '#14110E'

export type ProductNameConfig = {
  name: string
  // Editable nav page names (client-facing pages).
  pageMainName: string
  pageStatsName: string
  pagePlaybookName: string
  // Editable heading above the four Daily-Average mini-boxes on Stats.
  statsDailyAvgLabel: string
  // Label for the virtual "Unfiled" bucket (matters filed in no folder). Per-tenant
  // so a demo tenant can present it as "Personal"; defaults to "Unfiled" everywhere
  // else (e.g. MOVU is untouched).
  unfiledLabel: string
  // Five font groups. Brand carries the wordmark size; the rest are font-only.
  brandFont: FontKey
  brandSize: number
  recordFont: FontKey
  summaryFont: FontKey
  chromeFont: FontKey
  dataFont: FontKey
  // Sidebar label boldness toggles.
  boldFolder: boolean
  boldSubfolder: boolean
  boldMatter: boolean
  // pane-2 chronology subject/filename bold toggle.
  boldSubject: boolean
  // When Identifiers are exposed (Tweaks → IDENTIFIERS on), the sidebar
  // matter identifier line also shows the matter `kind`. This hides just the
  // kind segment while keeping the code (+ stage). Default TRUE — kind stays
  // hidden even with identifiers on (Ken 2026-08-11).
  hideMatterKind: boolean
  // The "I am selected" accent colour.
  selectedColor: string
  // Optional uploaded logo image (a `data:image/png|jpeg;base64,…` data URI,
  // set from QA → Product Name). When present the top-nav shows this image
  // instead of the text wordmark; the "Size — wordmark" slider controls its
  // height. Cleared → falls back to the text wordmark (`name`).
  brandLogo?: string
  // Optional uploaded image for the KEN AI chat circle (bottom-right). A
  // data:image/… URI set from QA → Product Name; when present the circle shows
  // this image as its surface instead of the flat nav colour. (Ken 2026-10-23)
  chatBubbleImage?: string
}

const KEY = 'me_product_name'
const EVT = 'me-product-name-change'
// One-time reset of the editable NAMES (wordmark + page labels) to this
// baseline for browsers that saved older values (e.g. wordmark 'Moravian').
// Preserves fonts/colours. Bump the tag to re-baseline again later.
const NAMES_BASELINE = '2026-08-11-matters'
// Font baseline — same pattern as NAMES_BASELINE but for the five font
// groups. Bumping the tag makes loadProductName() discard the four *Font
// keys from any older cached blob and fall through to DEFAULTS (or to
// server ui_config once initUiConfigSync resolves). Bumped 2026-08-17
// after finding stale 'instrument' (serif) values cached in browsers from
// the interval when the Google Fonts @import was silently dropped by
// Tailwind v4 and nobody noticed.
const FONTS_BASELINE = '2026-08-17-sans'
const DEFAULTS: ProductNameConfig = {
  name: 'Matters', pageMainName: 'Matters', pageStatsName: 'Stats', pagePlaybookName: 'Playbook',
  statsDailyAvgLabel: 'Professional Communications per Day',
  unfiledLabel: 'Unfiled',
  // Defaults chosen to match the look at the time of the 5-group cutover.
  brandFont: 'instrument', brandSize: 20,
  recordFont: 'inter',
  summaryFont: 'inter',
  chromeFont: 'inter',
  dataFont: 'noto',
  boldFolder: false, boldSubfolder: false, boldMatter: false,
  boldSubject: false, hideMatterKind: true, selectedColor: '#2563eb',
}

// Legacy keys (pre-5-group) → new group, so an existing localStorage blob
// migrates sensibly instead of snapping to defaults.
function migrate(raw: any, fk: (v: unknown, d: FontKey) => FontKey): Partial<ProductNameConfig> {
  const out: Partial<ProductNameConfig> = {}
  if (raw.font && !raw.brandFont) out.brandFont = fk(raw.font, DEFAULTS.brandFont)
  if (raw.size && !raw.brandSize) { const n = Number(raw.size); if (Number.isFinite(n)) out.brandSize = n }
  if (raw.connectedFont && !raw.recordFont) out.recordFont = fk(raw.connectedFont, DEFAULTS.recordFont)
  if (raw.matterFont && !raw.summaryFont) out.summaryFont = fk(raw.matterFont, DEFAULTS.summaryFont)
  if (raw.font2 && !raw.chromeFont) out.chromeFont = fk(raw.font2, DEFAULTS.chromeFont)
  if (raw.identifierFont && !raw.dataFont) out.dataFont = fk(raw.identifierFont, DEFAULTS.dataFont)
  return out
}

export function loadProductName(): ProductNameConfig {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '{}')
    const fk = (v: unknown, d: FontKey): FontKey =>
      typeof v === 'string' && Object.prototype.hasOwnProperty.call(FONTS, v) ? v as FontKey : d
    const sz = (v: unknown, d: number): number => {
      const n = Number(v)
      return Number.isFinite(n) && n >= 8 && n <= 96 ? n : d
    }
    const mig = migrate(raw, fk)
    // One-time NAME re-baseline: blobs saved before NAMES_BASELINE (e.g.
    // wordmark 'Moravian') reset the four editable names to defaults, keeping
    // fonts/colours. Persisted below so it happens once; saveProductName
    // re-stamps the flag on every write.
    const namesStale = raw._namesBaseline !== NAMES_BASELINE
    // Same idea for fonts: a cached blob without the current fonts baseline
    // stamp is treated as "font choices unknown" so we adopt DEFAULTS (which
    // server ui_config overwrites on the next fetch).
    const fontsStale = raw._fontsBaseline !== FONTS_BASELINE
    const rf = (k: 'brandFont' | 'recordFont' | 'summaryFont' | 'chromeFont' | 'dataFont') =>
      fontsStale ? undefined : (raw[k] ?? (mig as any)[k])
    const str = (v: unknown, d: string) => (typeof v === 'string' && v.trim() ? v : d)
    const cfg: ProductNameConfig = {
      name: namesStale ? DEFAULTS.name : str(raw.name, DEFAULTS.name),
      pageMainName: namesStale ? DEFAULTS.pageMainName : str(raw.pageMainName, DEFAULTS.pageMainName),
      pageStatsName: namesStale ? DEFAULTS.pageStatsName : str(raw.pageStatsName, DEFAULTS.pageStatsName),
      pagePlaybookName: namesStale ? DEFAULTS.pagePlaybookName : str(raw.pagePlaybookName, DEFAULTS.pagePlaybookName),
      // New editable field — defaults cleanly for blobs that predate it (not
      // tied to the names baseline; a missing value just adopts the default).
      statsDailyAvgLabel: str(raw.statsDailyAvgLabel, DEFAULTS.statsDailyAvgLabel),
      unfiledLabel: str(raw.unfiledLabel, DEFAULTS.unfiledLabel),
      brandFont: fk(rf('brandFont'), DEFAULTS.brandFont),
      brandSize: sz(raw.brandSize ?? mig.brandSize, DEFAULTS.brandSize),
      recordFont: fk(rf('recordFont'), DEFAULTS.recordFont),
      summaryFont: fk(rf('summaryFont'), DEFAULTS.summaryFont),
      chromeFont: fk(rf('chromeFont'), DEFAULTS.chromeFont),
      dataFont: fk(rf('dataFont'), DEFAULTS.dataFont),
      boldFolder: !!raw.boldFolder,
      boldSubfolder: !!raw.boldSubfolder,
      boldMatter: !!raw.boldMatter,
      boldSubject: !!raw.boldSubject,
      // Default TRUE (hidden) when absent, so kind hides even for existing
      // blobs that predate this pref.
      hideMatterKind: raw.hideMatterKind ?? true,
      selectedColor: typeof raw.selectedColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.selectedColor)
        ? raw.selectedColor : DEFAULTS.selectedColor,
      // Only trust an image data URI (png/jpeg/svg); anything else → no logo,
      // so the text wordmark shows.
      brandLogo: typeof raw.brandLogo === 'string' && raw.brandLogo.startsWith('data:image/')
        ? raw.brandLogo : undefined,
      chatBubbleImage: typeof raw.chatBubbleImage === 'string' && raw.chatBubbleImage.startsWith('data:image/')
        ? raw.chatBubbleImage : undefined,
    }
    if (namesStale) {
      try { localStorage.setItem(KEY, JSON.stringify({ ...raw, ...cfg, _namesBaseline: NAMES_BASELINE, _fontsBaseline: FONTS_BASELINE })) } catch { /* ignore */ }
    }
    return cfg
  } catch {
    return DEFAULTS
  }
}

export function saveProductName(cfg: ProductNameConfig): void {
  try { localStorage.setItem(KEY, JSON.stringify({ ...cfg, _namesBaseline: NAMES_BASELINE, _fontsBaseline: FONTS_BASELINE })) } catch { /* ignore */ }
  // The native `storage` event only fires in OTHER tabs, so dispatch our
  // own for live update within THIS tab (QA tool → nav wordmark).
  window.dispatchEvent(new CustomEvent(EVT))
  // Publish to the tenant's server-side config so every user of the tenant
  // sees it, not just this browser. Debounced + no-ops for non-writers /
  // before sync init, so this is safe to call on every QA edit.
  pushProductName(cfg)
}

// Apply the tenant's server-side product-name config into this browser
// (server wins over localStorage). Writes the cache + fires the live event so
// useProductName() consumers re-render; does NOT push back to the server
// (avoids an echo loop). Called once on app load from App.tsx.
export function applyServerProductName(raw: unknown): void {
  if (!raw || typeof raw !== 'object') return
  try {
    // Stamp the names baseline so loadProductName() treats the server's names
    // as current rather than re-baselining them to defaults.
    localStorage.setItem(KEY, JSON.stringify({ ...(raw as Record<string, unknown>), _namesBaseline: NAMES_BASELINE, _fontsBaseline: FONTS_BASELINE }))
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(EVT))
}

export function useProductName(): ProductNameConfig {
  const [cfg, setCfg] = useState<ProductNameConfig>(loadProductName)
  useEffect(() => {
    const reload = () => setCfg(loadProductName())
    window.addEventListener(EVT, reload)
    window.addEventListener('storage', reload)
    return () => {
      window.removeEventListener(EVT, reload)
      window.removeEventListener('storage', reload)
    }
  }, [])
  return cfg
}
